import { alpha } from '@mui/material';
import { palette } from '@client/theme/colors';
import { shadows } from '@client/theme/shadows';
import type { ThemeOptions } from '@mui/material';

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    overlay: true;
    paper: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    xsmall: true;
    xlarge: true;
    medium_inline: true;
  }
}

/*
  Merge these into the Button props to bypass typescript issues
  https://github.com/mui/material-ui/issues/32699
*/
const extraVariants: any = {
  colorSuccess: {
    backgroundColor: palette.success.main,
    color: palette.grey[50],

    '&:focus, &:active': {
      color: alpha(palette.grey[50], 0.8),
    },

    '&:hover': {
      backgroundColor: palette.success.light,
      boxShadow: shadows[4],
    },

    '&:active': {
      boxShadow: shadows[2],
    },

    '&:disabled': {
      backgroundColor: palette.success.light,
      color: palette.grey[50],
    },
  },
  colorError: {
    backgroundColor: palette.error.main,
    color: palette.grey[50],

    '&:focus, &:active': {
      color: alpha(palette.grey[50], 0.8),
    },

    '&:hover': {
      backgroundColor: palette.error.light,
      boxShadow: shadows[4],
    },

    '&:active': {
      boxShadow: shadows[2],
    },

    '&:disabled': {
      backgroundColor: palette.error.light,
      color: palette.grey[50],
    },
  },
  colorWarning: {
    backgroundColor: palette.warning.main,
    color: palette.grey[50],

    '&:focus, &:active': {
      color: alpha(palette.grey[50], 0.8),
    },

    '&:hover': {
      backgroundColor: palette.warning.light,
      boxShadow: shadows[4],
    },

    '&:active': {
      boxShadow: shadows[2],
    },

    '&:disabled': {
      backgroundColor: palette.warning.light,
      color: palette.grey[50],
    },
  },
  colorInfo: {
    backgroundColor: palette.info.main,
    color: palette.grey[50],

    '&:focus, &:active': {
      color: alpha(palette.grey[50], 0.8),
    },

    '&:hover': {
      backgroundColor: palette.info.light,
      boxShadow: shadows[4],
    },

    '&:active': {
      boxShadow: shadows[2],
    },

    '&:disabled': {
      backgroundColor: palette.info.light,
      color: palette.grey[50],
    },
  },
};

const IconButton: ThemeOptions['components'] = {
  MuiIcon: {
    styleOverrides: {
      root: {
        fontSize: 24,
      },
      fontSizeSmall: {
        fontSize: 18,
        lineHeight: '20px',
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      color: 'inherit',
    },
    styleOverrides: {
      root: {
        borderRadius: '50%',
        boxShadow: 'none',

        backgroundColor: palette.grey[300],
        color: palette.grey[500],

        '&:focus, &:active': {
          color: alpha(palette.grey[500], 0.8),
        },

        '&:hover': {
          backgroundColor: palette.grey[300],
          boxShadow: shadows[4],
        },

        '&:active': {
          boxShadow: shadows[2],
        },

        '&:disabled': {
          backgroundColor: palette.grey[300],
          color: palette.grey[400],
        },
      },
      colorInherit: {
        backgroundColor: 'transparent',
        color: palette.grey[500],

        '&:hover': {
          backgroundColor: 'transparent',
          color: palette.primary.light,
          boxShadow: 'none',
        },

        '&:focus, &:active': {
          color: palette.primary.main,
        },

        '&:disabled': {
          backgroundColor: 'transparent',
          color: palette.grey[400],
        },

        '&.active': { // NavLink styles for react-dom
          backgroundColor: 'transparent',
          color: palette.primary.main,

          '&:hover': {
            color: palette.primary.light,
          },

          '&:focus, &:active': {
            color: palette.primary.main,
          },
        },
      },
      colorPrimary: {
        backgroundColor: palette.primary.main,
        color: palette.grey[50],

        '&:focus, &:active': {
          color: alpha(palette.grey[50], 0.8),
        },

        '&:hover': {
          backgroundColor: palette.primary.light,
          boxShadow: shadows[4],
        },

        '&:active': {
          boxShadow: shadows[2],
        },

        '&:disabled': {
          backgroundColor: palette.primary.light,
          color: palette.grey[50],
        },
      },
      colorSecondary: {
        backgroundColor: palette.secondary.main,
        color: palette.grey[50],

        '&:focus, &:active': {
          color: alpha(palette.grey[50], 0.8),
        },

        '&:hover': {
          backgroundColor: palette.secondary.light,
          boxShadow: shadows[4],
        },

        '&:active': {
          boxShadow: shadows[2],
        },

        '&:disabled': {
          backgroundColor: palette.secondary.light,
          color: palette.grey[50],
        },
      },
      sizeSmall: {
        minWidth: 26,
        minHeight: 26,
        fontSize: 18,
        padding: 4,

        '& > svg': {
          fontSize: 18,
        },

        '&.MuiIconButton-edgeStart': {
          marginLeft: -4,
        },

        '&.MuiIconButton-edgeEnd': {
          marginRight: -4,
        },
      },
      sizeMedium: {
        minWidth: 32,
        minHeight: 32,
        fontSize: 24,
        padding: 4,

        '& > svg': {
          fontSize: 24,
        },

        '&.MuiIconButton-edgeStart': {
          marginLeft: -4,
        },

        '&.MuiIconButton-edgeEnd': {
          marginRight: -4,
        },
      },
      sizeLarge: {
        minWidth: 38,
        minHeight: 38,
        fontSize: 26,
        padding: 6,

        '& > svg': {
          fontSize: 26,
        },

        '&.MuiIconButton-edgeStart': {
          marginLeft: -6,
        },

        '&.MuiIconButton-edgeEnd': {
          marginRight: -6,
        },
      },
      ...extraVariants,
    },
    variants: [
      {
        props: { size: 'xsmall' },
        style: {
          minWidth: 18,
          minHeight: 18,
          fontSize: 16,
          padding: 1,

          '& > svg': {
            fontSize: 16,
          },

          '&.MuiIconButton-edgeStart': {
            marginLeft: -1,
          },

          '&.MuiIconButton-edgeEnd': {
            marginRight: -1,
          },
        },
      },
      {
        props: { size: 'medium_inline' },
        style: {
          minWidth: 24,
          minHeight: 24,
          fontSize: 20,
          padding: 1,

          '& > svg': {
            fontSize: 20,
          },

          '&.MuiIconButton-edgeStart': {
            marginLeft: -1,
          },

          '&.MuiIconButton-edgeEnd': {
            marginRight: -1,
          },
        },
      },
      {
        props: { size: 'xlarge' },
        style: {
          minWidth: 48,
          minHeight: 48,
          fontSize: 32,
          padding: 8,

          '& > svg': {
            fontSize: 32,
          },

          '&.MuiIconButton-edgeStart': {
            marginLeft: -8,
          },

          '&.MuiIconButton-edgeEnd': {
            marginRight: -8,
          },
        },
      },
      {
        props: { color: 'overlay' },
        style: {
          backgroundColor: 'transparent',
          color: palette.grey[50],

          '&:hover': {
            backgroundColor: 'transparent',
            color: alpha(palette.grey[50], 0.6),
            boxShadow: 'none',

            '& svg': {
              filter: `drop-shadow(${shadows[4]})`,
            },
          },

          '&:focus': {
            color: alpha(palette.grey[50], 0.8),
          },

          '&:active': {
            color: palette.grey[50],
          },

          '&:disabled': {
            backgroundColor: 'transparent',
            color: palette.grey[400],
          },
        },
      },
      {
        props: { color: 'paper' },
        style: {
          backgroundColor: palette.background.paper,
          color: palette.grey[600],

          '&:focus, &:active': {
            color: palette.grey[500],
          },

          '&:hover': {
            backgroundColor: palette.background.paper,
            boxShadow: shadows[4],
            color: palette.grey[500],
          },

          '&:active': {
            boxShadow: shadows[2],
            color: palette.grey[600],
          },

          '&:disabled': {
            backgroundColor: palette.background.paper,
            color: palette.grey[400],
          },
        },
      },
    ],
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        verticalAlign: 'middle',

        '& + span': {
          verticalAlign: 'middle',
        },

        '&.feather-icons': {
          fill: 'none',
        },
      },
      fontSizeSmall: {
        lineHeight: '20px',
      },
    },
  },
};

export default IconButton;
